import { normalizeImportCategory } from './importLogsParsingClassification';
import {
  CARE_MEDICATION_DOSE_MG_PATTERN,
  CARE_MEDICATION_IMPORT_NAME_PATTERN,
} from '../../../shared/careMedicationPatterns';
import { buildConciseBehaviorTitle } from '../../../utils/behaviorTitles';
import { buildConciseMilestoneTitle } from '../../../utils/milestoneTitles';
import { CARE_EXPLICIT_TIME_PATTERN } from '../../../shared/careDateTimePatterns';
import { toSentenceCase } from './importLogsRelationshipConnectors';

const titleCase = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const firstClause = (value = '') => String(value || '')
  .trim()
  .replace(/\s+/g, ' ')
  .split(/(?<=[.!?])\s+/)[0]
  ?.split(/\s*[,:;]\s+/)[0]
  ?.trim() || '';

export const isGenericResolvedTitle = (title = '', category = '') => {
  const normalizedTitle = String(title || '').trim().toLowerCase();
  const normalizedCategory = String(category || '').trim().toLowerCase();

  return !normalizedTitle
    || normalizedTitle === normalizedCategory
    || normalizedTitle === 'behavior'
    || normalizedTitle === 'medication'
    || normalizedTitle === 'meds'
    || normalizedTitle === 'food'
    || normalizedTitle === 'sleep'
    || normalizedTitle === 'activity'
    || normalizedTitle === 'mood'
    || normalizedTitle === 'toilet'
    || normalizedTitle === 'bathroom'
    || normalizedTitle === 'log'
    || normalizedTitle === 'note'
    || normalizedTitle === 'daily log'
    || normalizedTitle === 'medication log'
    || normalizedTitle === 'behavior log'
    || normalizedTitle === 'activity log'
    || normalizedTitle === 'mood log';
};

const buildMedicationResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Medication';

  const reverseStatusMatch = source.match(/\b([A-Z][a-zA-Z]+)\s+(stopped|started|restarted|held|missed)\b/i);
  if (reverseStatusMatch) {
    const med = titleCase(reverseStatusMatch[1]);
    const action = reverseStatusMatch[2].toLowerCase();
    return `${med} ${action}`;
  }

  const statusMatch = source.match(/\b(increased|decreased|stopped|started|restarted|held|missed)\s+([A-Z][a-zA-Z]+)/i);
  if (statusMatch) {
    const action = titleCase(statusMatch[1]);
    const med = titleCase(statusMatch[2]);
    return `${action} ${med}`;
  }

  if (/evening|bedtime|night/i.test(source)) return 'Evening meds';

  const doseTimeMatch = source.match(/\b([A-Z][a-zA-Z]+)\s+(Morning|Evening)\s+Dose\b/i);
  if (doseTimeMatch) {
    return `${titleCase(doseTimeMatch[1])} ${titleCase(doseTimeMatch[2])} dose`;
  }

  const medNameMatch = source.match(CARE_MEDICATION_IMPORT_NAME_PATTERN);
  if (medNameMatch?.[0]) {
    const doseMatch = source.match(CARE_MEDICATION_DOSE_MG_PATTERN);
    return doseMatch ? `${titleCase(medNameMatch[0])} ${doseMatch[0]}` : titleCase(medNameMatch[0]);
  }

  return 'Medication';
};

const buildSleepResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Sleep';

  const wakeTimeMatch = source.match(new RegExp(`\\bwakes?\\s+up\\s+at\\s+(${CARE_EXPLICIT_TIME_PATTERN})\\b`, 'i'));
  if (wakeTimeMatch?.[1]) return `Woke up at ${wakeTimeMatch[1].toUpperCase().replace(/\s+/g, ' ')}`;
  if (/\bwakes?\s+up\b/i.test(source) || /\bwoke up\b/i.test(source) || /\bwoke\b/i.test(source)) return 'Woke up';
  if (/\bsleep duration\b/i.test(source)) return 'Sleep duration';
  if (/\bfell asleep\b/i.test(source)) return 'Fell asleep';
  if (/\bbedtime\b/i.test(source)) return 'Bedtime';
  if (/\brough night\b/i.test(source)) return 'Rough night';
  if (/\bnap\b/i.test(source)) return 'Nap';
  return 'Sleep';
};

const buildFoodResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Food';
  if (/\bbreakfast\b/i.test(source)) return 'Breakfast';
  if (/\blunch\b/i.test(source)) return 'Lunch';
  if (/\bdinner\b/i.test(source)) return 'Dinner';
  if (/\bsnack\b/i.test(source)) return 'Snack';
  if (/\bwater bottle\b/i.test(source)) return 'Before school';
  if (/\bjuice\b/i.test(source)) return 'Juice';
  if (/\bappetite\b/i.test(source)) return 'Appetite change';
  return 'Food';
};

const buildHealthResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Health';
  if (/\bappointment\b/i.test(source)) {
    if (/\bneurolog(?:y|ist)\b/i.test(source)) return 'Neurology appointment';
    if (/\bpsychiatr(?:y|ist)\b/i.test(source)) return 'Psychiatry appointment';
    if (/\bdoctor\b/i.test(source)) return 'Doctor appointment';
    return 'Appointment';
  }
  if (/\b(?:tired|fatigue|fatigued)\b/i.test(source) && /\bappetite\b/i.test(source)) return 'Fatigue and appetite change';
  if (/\bappetite\b/i.test(source)) return 'Appetite change';
  if (/\b(?:sleepy|groggy|sluggish|tired|fatigue|fatigued)\b/i.test(source)) {
    const symptomMatch = source.match(/\b(sleepy|groggy|sluggish|tired|fatigued)\b(?:,\s*\b(sleepy|groggy|sluggish|tired|fatigued)\b)?/i);
    if (symptomMatch?.[1]) {
      const first = toSentenceCase(symptomMatch[1].toLowerCase());
      const second = symptomMatch[2] ? symptomMatch[2].toLowerCase() : '';
      return second ? `${first} and ${second}` : first;
    }
  }
  return 'Health';
};

const buildActivityResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Activity';
  if (/\bmusic therapy\b/i.test(source)) return 'Music therapy';
  if (/\bgroup reading\b/i.test(source)) return 'Group reading';
  if (/\bgroup painting\b/i.test(source)) return 'Group painting';
  if (/\bparticipat/i.test(source) && /\bprogress|improv(?:ed|ement)|better\b/i.test(source)) return 'Participation progress';
  if (/\bprogress|improv(?:ed|ement)|success(?:ful)?|proud|accomplishment|great job|did well|better than|nice improvement\b/i.test(source)) return 'Progress';
  if (/\bfocused\b/i.test(source)) return 'Focused work';
  if (/\bart class\b/i.test(source)) return 'Art class';
  if (/\brecess\b/i.test(source)) return 'Recess';
  if (/\bschool pickup\b/i.test(source)) return 'School pickup';
  if (/\bplayed independently\b/i.test(source)) return 'Independent play';
  if (/\bswings?\b/i.test(source)) return 'Swing time';
  if (/\bworking with the other kids\b/i.test(source)) return 'Working with peers';
  if (/\bschool\b/i.test(source)) return 'School activity';
  if (/\bproject\b/i.test(source)) return 'Group project';
  if (/\bparticipat/i.test(source)) return 'Participation';
  return 'Activity';
};

const buildMoodResolvedTitle = (text = '') => {
  const source = String(text || '').trim();
  if (!source) return 'Mood';
  const moodMatch = source.match(/\b(happy|affectionate|tired|anxious|worried|excited|calm|frustrated|upset|sad|content|nervous|proud|mixed|overstimulated|more relaxed)\b/i);
  if (moodMatch?.[1]) return titleCase(moodMatch[1]);
  if (/\blaughed\b/i.test(source)) return 'Happy';
  return 'Mood';
};

export const buildResolvedEntryTitle = (category = 'note', text = '', {
  titleHint = '',
} = {}) => {
  const normalizedCategory = normalizeImportCategory(category || 'log');
  const comparable = String(text || '').trim();
  const normalizedHint = String(titleHint || '').trim();

  if (normalizedHint && !isGenericResolvedTitle(normalizedHint, normalizedCategory)) {
    return normalizedHint;
  }

  switch (normalizedCategory) {
    case 'behavior':
      return buildConciseBehaviorTitle(comparable) || 'Behavior incident';
    case 'medication':
      return buildMedicationResolvedTitle(comparable);
    case 'sleep':
      return buildSleepResolvedTitle(comparable);
    case 'food':
      return buildFoodResolvedTitle(comparable);
    case 'health':
      return buildHealthResolvedTitle(comparable);
    case 'activity':
      return buildActivityResolvedTitle(comparable);
    case 'mood':
      return buildMoodResolvedTitle(comparable);
    case 'milestone':
      return buildConciseMilestoneTitle(comparable) || 'Milestone';
    default:
      return firstClause(comparable) || 'Note';
  }
};

const buildMedicationSplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  const medicationMatch = comparable.match(/\b(?:Clonidine|Guanfacine|Melatonin|Sertraline|Adderall|Vyvanse|Concerta|Ritalin|Focalin|Strattera|Methylphenidate|Dexedrine)\b/i);
  const medicationName = medicationMatch ? medicationMatch[0] : 'Medication';

  if (/\bincreas(?:e|ed|ing)\b/i.test(comparable)) return `${medicationName} increased`;
  if (/\bdecreas(?:e|ed|ing)\b/i.test(comparable)) return `${medicationName} decreased`;
  if (/\bstart(?:ed|ing)?\b/i.test(comparable)) return `${medicationName} started`;
  if (/\bstopp(?:ed|ing)?\b/i.test(comparable)) return `${medicationName} stopped`;
  if (/\brefus(?:ed|ing)\b/i.test(comparable)) return `${medicationName} refused`;
  if (/\b(?:gave|given|administered|took)\b/i.test(comparable)) return `${medicationName} given`;

  return `${medicationName} update`;
};

const buildHealthSplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  if (/\b(?:tired|fatigue|fatigued)\b/i.test(comparable) && /\bappetite\b/i.test(comparable)) {
    return 'Fatigue and appetite change';
  }
  if (/\bappetite\b/i.test(comparable)) return 'Appetite change';
  if (/\b(?:tired|fatigue|fatigued)\b/i.test(comparable)) return 'Fatigue';
  return 'Health note';
};

const buildSleepSplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  if (/\brough night\b/i.test(comparable)) return 'Rough night';
  if (/\bfalling asleep\b/i.test(comparable)) return 'Falling asleep';
  if (/\bwoke up\b/i.test(comparable)) return 'Woke up upset';
  return 'Sleep note';
};

const buildFoodSplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  const mealMatch = comparable.match(/\b(?:breakfast|lunch|dinner|snack)\b/i);
  if (mealMatch) {
    return toSentenceCase(mealMatch[0]);
  }
  if (/\bappetite\b/i.test(comparable)) return 'Appetite change';
  return 'Food update';
};

const buildActivitySplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  if (/\bschool\b/i.test(comparable)) return 'School activity';
  if (/\bproject\b/i.test(comparable)) return 'Group project';
  return 'Activity update';
};

const buildMoodSplitTitle = (text = '') => {
  const comparable = String(text || '').trim();
  if (/\btired\b/i.test(comparable)) return 'Tired';
  if (/\banxious\b/i.test(comparable)) return 'Anxious';
  if (/\bhappy\b/i.test(comparable)) return 'Happy';
  return 'Mood note';
};

export const buildSplitEntryTitle = (category = 'note', text = '') => {
  const normalizedCategory = normalizeImportCategory(category || 'log');
  const comparable = String(text || '').trim();
  switch (normalizedCategory) {
    case 'behavior':
      return buildConciseBehaviorTitle(comparable) || 'Behavior incident';
    case 'medication':
      return buildMedicationSplitTitle(comparable);
    case 'sleep':
      return buildSleepSplitTitle(comparable);
    case 'food':
      return buildFoodSplitTitle(comparable);
    case 'health':
      return buildHealthSplitTitle(comparable);
    case 'activity':
      return buildActivitySplitTitle(comparable);
    case 'mood':
      return buildMoodSplitTitle(comparable);
    case 'milestone':
      return buildConciseMilestoneTitle(comparable) || 'Milestone';
    default:
      return comparable || 'Note';
  }
};
